import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSaveRunAsPart } from "../services/PartService";
import {
  useListPartGroups,
  type PartGroup,
} from "../services/PartGroupService";
import { useHistory } from "react-router-dom";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";

interface SaveAsPartModalProps {
  open: boolean;
  onClose: () => void;
  runId: number;
}

const SaveAsPartModal = ({ open, onClose, runId }: SaveAsPartModalProps) => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [partType, setPartType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [description, setDescription] = useState("");
  const [partGroupFk, setPartGroupFk] = useState<number | "">("");

  const { data: partGroups } = useListPartGroups();
  const saveAsPart = useSaveRunAsPart();

  const handleSubmit = () => {
    if (!name.trim()) return;
    saveAsPart.mutate(
      {
        runId,
        payload: {
          name: name.trim(),
          part_type: partType.trim() || null,
          manufacturer: manufacturer.trim() || null,
          description: description.trim() || null,
          part_group_fk: partGroupFk === "" ? null : partGroupFk,
        },
      },
      {
        onSuccess: (part) => {
          handleReset();
          onClose();
          history.push(`/parts/detail/${part.id}`);
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error("Failed to save run as part", err);
          NucToast({
            message: `Failed to save as part: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
        },
      },
    );
  };

  const handleReset = () => {
    setName("");
    setPartType("");
    setManufacturer("");
    setDescription("");
    setPartGroupFk("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saveAsPart.isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Save as Part</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save this enrichment result as a part in the inventory. The feature
          sheet will be carried over automatically.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Part Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Part Type"
            value={partType}
            onChange={(e) => setPartType(e.target.value)}
            fullWidth
            placeholder="e.g., Valve, Flange, Pump"
          />
          <TextField
            label="Manufacturer"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Part Group</InputLabel>
            <Select
              value={partGroupFk}
              label="Part Group"
              onChange={(e) => {
                const raw = e.target.value;
                setPartGroupFk(raw === "" ? "" : Number(raw));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {partGroups?.map((g: PartGroup) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saveAsPart.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || saveAsPart.isLoading}
          startIcon={
            saveAsPart.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {saveAsPart.isLoading ? "Saving..." : "Save as Part"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveAsPartModal;
